import { KeragonWorkflow } from "../../../../common/entities/keragon";

/**
 * Pure rules behind the Keragon workflows settings card, kept out of the
 * component so they can be unit-tested without React.
 */

export type WorkflowForm = { name: string; URL: string };
export type WorkflowFormErrors = { name?: string; URL?: string };

export const MAX_WORKFLOW_NAME_LENGTH = 60;

const normaliseUrl = (url: string | null | undefined) =>
  String(url ?? "").trim().toLowerCase();
// The backend trigger proxy only forwards to keragon.com hosts.
const KERAGON_HOST = "keragon.com";
export const isKeragonHost = (hostname: string) => {
  const host = hostname.toLowerCase();
  return host === KERAGON_HOST || host.endsWith(`.${KERAGON_HOST}`);
};

const normaliseName = (name: string | null | undefined) =>
  String(name ?? "").trim().toLowerCase();

/** Field-level validation; an empty object means the form can be saved. */
export const validateWorkflowForm = (
  form: WorkflowForm,
  existing: KeragonWorkflow[],
  editingId: string | null
): WorkflowFormErrors => {
  const errors: WorkflowFormErrors = {};
  const name = form.name.trim();
  const url = form.URL.trim();

  if (!name) {
    errors.name = "Give this connection a name.";
  } else if (name.length > MAX_WORKFLOW_NAME_LENGTH) {
    errors.name = `Keep the name under ${MAX_WORKFLOW_NAME_LENGTH} characters.`;
  } else if (
    existing.some(
      (w) => w.id !== editingId && normaliseName(w.name) === normaliseName(name)
    )
  ) {
    errors.name = "A connection with this name already exists.";
  }

  if (!url) {
    errors.URL = "Paste the webhook URL Keragon generated.";
  } else {
    let parsed: URL | null = null;
    try {
      parsed = new URL(url);
    } catch {
      parsed = null;
    }
    if (!parsed || parsed.protocol !== "https:") {
      errors.URL = "Enter a valid https:// webhook URL.";
    } else if (!isKeragonHost(parsed.hostname)) {
      errors.URL = "Enter a Keragon webhook URL (https://…keragon.com/…).";
    } else if (
      existing.some(
        (w) => w.id !== editingId && normaliseUrl(w.URL) === normaliseUrl(url)
      )
    ) {
      errors.URL = "This webhook URL is already connected.";
    }
  }

  return errors;
};

/** The first saved workflow is the default; editing keeps the stored flag. */
export const nextActiveFlag = (
  existing: KeragonWorkflow[],
  editing: KeragonWorkflow | null
): 0 | 1 => {
  if (editing) return editing.active === 1 ? 1 : 0;
  return existing.length === 0 ? 1 : 0;
};

export const getDefaultWorkflow = (
  workflows: KeragonWorkflow[]
): KeragonWorkflow | undefined => workflows.find((w) => w.active === 1);

/**
 * Switching the default: activate the target, deactivate every other active
 * workflow (normally one — but the backend does not enforce exclusivity, so a
 * partial failure can leave two, and this heals it).
 */
export const planDefaultSwitch = (
  workflows: KeragonWorkflow[],
  targetId: string
): { activate: KeragonWorkflow; deactivate: KeragonWorkflow[] } | null => {
  const activate = workflows.find((w) => w.id === targetId);
  if (!activate) return null;
  return {
    activate,
    deactivate: workflows.filter((w) => w.id !== targetId && w.active === 1),
  };
};

/**
 * Deleting the default while others remain promotes another one, so the
 * affiliate is never left without a default. Picks the first remaining in list
 * order (the backend returns them in creation order).
 */
export const planDelete = (
  workflows: KeragonWorkflow[],
  id: string
): { target: KeragonWorkflow; promote: KeragonWorkflow | null } | null => {
  const target = workflows.find((w) => w.id === id);
  if (!target) return null;
  const remaining = workflows.filter((w) => w.id !== id);
  const promote =
    target.active === 1 && remaining.length > 0 && !remaining.some((w) => w.active === 1)
      ? remaining[0]
      : null;
  return { target, promote };
};
